import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { H3 } from '@mxenabled/mxui'

import { useAppConfig, useGlobalCopyStore, useSettingsStore, useUserStore } from '../../../common'
import { type EditableUser } from '../../../core'
import { useDimensions } from '../../../common/hooks/useDimensions'

import VerifyEmailAction from '../profile/actions/VerifyEmailAction'
import VerifyPhoneAction from '../profile/actions/VerifyPhoneAction'

const PersonalDetails = () => {
  const { config } = useAppConfig()
  const [containerRef, { width }] = useDimensions()
  const { common: commonCopy, settings: settingsCopy } = useGlobalCopyStore()
  const { updateUser } = useSettingsStore()
  const { user } = useUserStore()

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [modifiedUser, setModifiedUser] = React.useState<EditableUser>({
    email: user.email,
    phone: user.phone,
  })

  const showVerifyEmail = modifiedUser.email && !user.email_is_verified && !hasUnsavedChanges
  const showVerifyPhone = modifiedUser.phone && !user.phone_is_verified && !hasUnsavedChanges

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const changedUser = { ...modifiedUser, [name]: value }

    setModifiedUser(changedUser)
    setHasUnsavedChanges(JSON.stringify({ ...user, ...changedUser }) !== JSON.stringify(user))
  }

  const handleSave = async () => {
    await updateUser({ ...user, ...modifiedUser })
    setHasUnsavedChanges(false)
  }

  return (
    <Stack ref={containerRef}>
      <Divider />
      <Stack sx={{ pt: 12 }}>
        <H3 sx={{ lineHeight: '20px', pb: 4, pt: 16 }}>{settingsCopy.personal_details}</H3>
        <Stack sx={{ flexDirection: width <= 768 ? 'column' : 'row', gap: 24, py: 16 }}>
          {config.display_email_edit_field_in_settings && (
            <Stack sx={{ flex: 1, pb: 14 }}>
              <TextField
                defaultValue={user.email}
                fullWidth={true}
                label={settingsCopy.email_address}
                name="email"
                onChange={handleChange}
              />
              {showVerifyEmail && (
                <Box
                  sx={{
                    height: 20,
                    '.MuiButton-root': {
                      px: 0,
                      ':hover': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                >
                  <VerifyEmailAction />
                </Box>
              )}
            </Stack>
          )}
          {config.display_sms_edit_field_in_settings && (
            <Stack sx={{ flex: 1, pb: 14 }}>
              <TextField
                defaultValue={user.phone}
                fullWidth={true}
                label={settingsCopy.mobile_number}
                name="phone"
                onChange={handleChange}
              />
              {showVerifyPhone && (
                <Box
                  sx={{
                    height: 20,
                    '.MuiButton-root': {
                      px: 0,
                      ':hover': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                >
                  <VerifyPhoneAction />
                </Box>
              )}
            </Stack>
          )}
        </Stack>
        <Button
          disabled={!hasUnsavedChanges}
          onClick={handleSave}
          sx={{ width: 84 }}
          variant="contained"
        >
          {commonCopy.save_button}
        </Button>
      </Stack>
    </Stack>
  )
}

export default observer(PersonalDetails)
